import { useState, ChangeEvent, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import SearchIcon from '@mui/icons-material/Search';
import Chip from '@mui/material/Chip';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { Trans, useTranslation } from 'react-i18next';
import { ScreensEntity, useGetScreensQuery } from '@/models/company-screens';
import dayjs from 'dayjs';
import { CameraState } from '@/models/company-screens/type';

enum Status {
  All = 'ALL',
  Online = 'ONLINE',
  Offline = 'OFFLINE',
}

export const TaxiTable = () => {
  const [input, setInput] = useState<string | undefined>();
  const [status, setStatus] = useState('ALL');
  const [screens, setScreens] = useState<ScreensEntity[]>([]);
  const { t } = useTranslation();
  const { data, isLoading } = useGetScreensQuery();

  useEffect(() => {
    let result: ScreensEntity[] | undefined;

    if (input && status === Status.All) {
      result = data?.result?.filter((it) => {
        return it.carRegNr
          .toLocaleLowerCase()
          .includes(input.toLocaleLowerCase());
      });
    }

    if (!input && status === Status.All) {
      result = data?.result || [];
    }

    if (!input && status !== Status.All) {
      result = data?.result?.filter((it) => {
        return it.status === status;
      });
    }

    if (input && status !== Status.All) {
      result = data?.result?.filter((it) => {
        return (
          it.carRegNr.toLocaleLowerCase().includes(input.toLocaleLowerCase()) &&
          it.status === status
        );
      });
    }

    setScreens(result || []);
  }, [input, status, data]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    setStatus(event.target.value);
  };

  const DataRow = ({ screen }: { screen: ScreensEntity }) => {
    const lastOnline =
      screen.status === Status.Online
        ? t('label.last_online_moment_ago')
        : screen.last_online
          ? dayjs(screen.last_online).format('DD/MM/YYYY hh:mm:ss')
          : 'N/A';

    return (
      <TableRow
        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
        className="hover:bg-slate-50 hover:cursor-pointer"
      >
        <TableCell align="left">{screen.carRegNr}</TableCell>
        <TableCell align="left">{screen.screen_id}</TableCell>
        <TableCell align="left">{screen.passengers}</TableCell>

        <TableCell align="left">
          {screen.status === Status.Online ? (
            <Chip label="Online" variant="filled" color="success" />
          ) : (
            <Chip label="Offline" variant="filled" color="error" />
          )}
        </TableCell>

        <TableCell align="left">{lastOnline}</TableCell>
        <TableCell align="left">{screen.brand}</TableCell>
        <TableCell align="left">{screen.model}</TableCell>
        <TableCell align="left">{screen.year}</TableCell>
        <TableCell align="left">
          {screen.active === true ? (
            <Chip label="Activated" variant="filled" color="success" />
          ) : (
            <Chip label="Not activated" variant="filled" color="error" />
          )}
        </TableCell>
        <TableCell align="left">
          <CameraStateChip state={screen.camera_state} />
        </TableCell>
      </TableRow>
    );
  };

  const EmptyRow = () => (
    <TableRow
      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
      className="hover:bg-slate-50 hover:cursor-pointer"
    >
      <TableCell align="center" colSpan={6}>
        {t('taxi.no_match')}
      </TableCell>
    </TableRow>
  );

  return (
    <div
      className="bg-white rounded-2xl p-6 w-full h-full relative overflow-hidden"
      style={{
        boxShadow:
          'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
        minHeight: '100px',
      }}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="w-52 mr-3">
          <FormControl fullWidth>
            <InputLabel id="status-select-label">
              {t('label.online_status')}
            </InputLabel>
            <Select
              labelId="status-select-label"
              id="status-select"
              value={status}
              label={t('label.online_status')}
              onChange={handleStatusChange}
            >
              <MenuItem value={Status.All}>{t('label.all')}</MenuItem>
              <MenuItem value={Status.Online}>{t('label.online')}</MenuItem>
              <MenuItem value={Status.Offline}>{t('label.offline')}</MenuItem>
            </Select>
          </FormControl>
        </div>
        <div className="flex items-center justify-center w-full p-3 bg-white border border-slate-300 rounded-[5px] ">
          <SearchIcon style={{ marginRight: 10 }} />
          <input
            value={input}
            type="text"
            className="w-full bg-transparent outline-hidden h-8"
            onChange={handleInputChange}
            placeholder={t('label.search_car')}
          />
        </div>
      </div>
      <div>
        <div className="rounded-lg overflow-hidden">
          <TableContainer sx={{ minHeight: 500 }}>
            <Table aria-label="simple table">
              <TableHead className=" bg-black ">
                <TableRow style={{ color: 'white' }}>
                  <TableCell align="left" style={{ color: 'white' }}>
                    <Trans i18nKey="label.car_reg" />
                  </TableCell>
                  <TableCell align="left" style={{ color: 'white' }}>
                    <Trans i18nKey="label.screen_id" />
                  </TableCell>
                  <TableCell align="left" style={{ color: 'white' }}>
                    <Trans i18nKey="label.views" />
                  </TableCell>
                  <TableCell align="left" style={{ color: 'white' }}>
                    <Trans i18nKey="label.online_status" />
                  </TableCell>
                  <TableCell align="left" style={{ color: 'white' }}>
                    <Trans i18nKey="label.last_online" />
                  </TableCell>
                  <TableCell align="left" style={{ color: 'white' }}>
                    <Trans i18nKey="label.car_brand" />
                  </TableCell>
                  <TableCell align="left" style={{ color: 'white' }}>
                    <Trans i18nKey="label.car_model" />
                  </TableCell>
                  <TableCell align="left" style={{ color: 'white' }}>
                    <Trans i18nKey="label.car_year_of_production" />
                  </TableCell>
                  <TableCell align="left" style={{ color: 'white' }}>
                    <Trans i18nKey="label.screen_activation_status" />
                  </TableCell>
                  <TableCell align="left" style={{ color: 'white' }}>
                    <Trans i18nKey="label.camera_status" />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {screens.map((screen) => (
                  <DataRow screen={screen} key={screen.id} />
                ))}
                {isLoading === false && screens.length === 0 && <EmptyRow />}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </div>
  );
};

const config = {
  NOT_AVAILABLE: {
    label: 'Not Available',
    bg: 'bg-gray-400/30',
    color: 'text-gray-700',
  },
  BLIND: {
    label: 'Blind',
    bg: 'bg-red-400/30',
    color: 'text-red-700',
  },
  HEALTHY: {
    label: 'Healthy',
    bg: 'bg-green-400/30',
    color: 'text-green-700',
  },
};

export const CameraStateChip = ({ state }: { state: CameraState }) => {
  const status = config[state];

  return (
    <div
      className={`${status.bg} ${status.color} px-2 py-2 rounded-full text-center text-nowrap`}
    >
      {status.label}
    </div>
  );
};
